import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api, ApiClientError, type OrderDto } from '../lib/api';
import { usePageMeta } from '../lib/meta';
import { formatDate, price, priceOrFree } from '../lib/format';
import { Alert, Button, EmptyState, Spinner } from '../components/ui';

export function OrderPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const [params] = useSearchParams();

  const [order, setOrder] = useState<OrderDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [needsEmail, setNeedsEmail] = useState(false);
  const [emailInput, setEmailInput] = useState(params.get('email') ?? '');
  const [error, setError] = useState<string | null>(null);

  usePageMeta({ title: order ? `Order ${order.orderNumber}` : 'Your order' });

  const load = (email?: string) => {
    if (!orderId) return;
    setLoading(true);
    setError(null);

    void api
      .order(orderId, email)
      .then((response) => {
        setOrder(response.order);
        setNeedsEmail(false);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiClientError && err.status === 403) {
          // The order exists but we cannot prove it belongs to this visitor.
          setNeedsEmail(true);
        } else {
          setError('We could not find that order.');
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(params.get('email') ?? undefined);
    // Re-running on every param change would refetch as the user types.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  if (loading) {
    return (
      <div className="page-container flex justify-center py-32">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  if (needsEmail) {
    return (
      <div className="page-container max-w-md py-20">
        <h1 className="text-2xl">Find your order</h1>
        <p className="mt-3 text-sm text-muted">
          For your privacy, please confirm the email address used for this order.
        </p>
        <form
          className="mt-6 space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            load(emailInput);
          }}
        >
          <input
            type="email"
            required
            value={emailInput}
            onChange={(event) => setEmailInput(event.target.value)}
            placeholder="you@example.com"
            aria-label="Email address"
            className="w-full border border-border bg-transparent px-3 py-2.5 text-sm outline-none focus:border-ink"
          />
          <Button type="submit" className="w-full">
            View order
          </Button>
        </form>
      </div>
    );
  }

  if (error || !order) {
    return (
      <EmptyState
        title="Order not found"
        message={error ?? 'That order could not be found.'}
        action={
          <Link to="/" className="text-sm underline underline-offset-4">
            Back to the shop
          </Link>
        }
      />
    );
  }

  const paid = order.payment.status === 'succeeded';
  const currency = order.currency;

  return (
    <div className="page-container max-w-3xl py-12">
      <div className="border border-border p-8 text-center">
        <p className="text-3xl" aria-hidden="true">
          {paid ? '✦' : '⋯'}
        </p>
        <h1 className="mt-3 text-2xl">
          {paid ? 'Thank you — your order is confirmed' : 'Your order is awaiting payment'}
        </h1>
        <p className="mt-3 text-sm text-muted">
          Order <strong className="text-ink">{order.orderNumber}</strong> ·{' '}
          {formatDate(order.createdAt)}
        </p>
        <p className="mt-1 text-sm text-muted">
          A confirmation has been sent to {order.email}.
        </p>
      </div>

      {order.payment.provider === 'test' && (
        <div className="mt-6">
          <Alert tone="info">
            This order was placed with the test payment provider — no money has changed
            hands. Connect Stripe to take live payments.
          </Alert>
        </div>
      )}

      <section className="mt-10">
        <h2 className="mb-4 border-b border-border pb-2 text-lg">Your pieces</h2>
        <ul className="divide-y divide-border">
          {order.lines.map((line) => (
            <li key={line.productId} className="flex items-center gap-4 py-4">
              <Link
                to={`/products/${line.slug}`}
                className="h-20 w-16 shrink-0 overflow-hidden bg-surface"
              >
                {line.imageUrl && (
                  <img src={line.imageUrl} alt="" className="h-full w-full object-cover" />
                )}
              </Link>
              <div className="flex-1">
                <Link to={`/products/${line.slug}`} className="text-sm hover:underline">
                  {line.title.split('|')[0]?.trim() ?? line.title}
                </Link>
                <p className="mt-1 text-xs text-muted">
                  Quantity {line.quantity} · {price(line.unitPriceMinor, currency)} each
                </p>
              </div>
              <p className="text-sm">{price(line.lineTotalMinor, currency)}</p>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-8 grid gap-8 sm:grid-cols-2">
        <div>
          <h2 className="mb-3 text-xs uppercase tracking-widest text-muted">
            Delivering to
          </h2>
          <address className="text-sm not-italic leading-relaxed">
            {order.shippingAddress.name}
            <br />
            {order.shippingAddress.line1}
            <br />
            {order.shippingAddress.line2 && (
              <>
                {order.shippingAddress.line2}
                <br />
              </>
            )}
            {order.shippingAddress.city}
            <br />
            {order.shippingAddress.county && (
              <>
                {order.shippingAddress.county}
                <br />
              </>
            )}
            {order.shippingAddress.postcode}
            <br />
            {order.shippingAddress.countryCode}
          </address>
          <p className="mt-3 text-xs text-muted">Via {order.shippingRateName}</p>
        </div>

        <dl className="space-y-2 text-sm">
          <div className="flex justify-between">
            <dt className="text-muted">Subtotal</dt>
            <dd>{price(order.subtotalMinor, currency)}</dd>
          </div>
          {order.discountMinor > 0 && (
            <div className="flex justify-between text-sale">
              <dt>
                Discounts
                <span className="block text-[10px] uppercase tracking-wider opacity-80">
                  {order.appliedDiscounts.map((d) => d.title).join(', ')}
                </span>
              </dt>
              <dd>−{price(order.discountMinor, currency)}</dd>
            </div>
          )}
          <div className="flex justify-between">
            <dt className="text-muted">Delivery</dt>
            <dd>
              {order.shippingDiscountMinor > 0
                ? 'Free'
                : priceOrFree(order.shippingMinor, currency)}
            </dd>
          </div>
          <div className="flex justify-between border-t border-border pt-3 text-lg">
            <dt>Total</dt>
            <dd className="font-bold">{price(order.totalMinor, currency)}</dd>
          </div>
        </dl>
      </section>

      <div className="mt-12 flex justify-center gap-3">
        <Link
          to="/collections/all-jewellery"
          className="text-sm underline underline-offset-4"
        >
          Continue shopping
        </Link>
      </div>
    </div>
  );
}
